<template>
  <q-dialog v-model="open" content-class="z-max">
    <q-card class="refine-card">
      <q-card-section class="row items-center no-wrap">
        <div class="text-h6">Refine Selection</div>
        <q-space />
        <q-btn dense flat round icon="close" @click="cancel" />
      </q-card-section>

      <q-separator />

      <q-card-section>
        <div class="tools row items-center justify-between q-gutter-sm q-mb-sm">
          <div class="row items-center q-gutter-sm">
            <q-btn
              :color="mode === 'brush' ? 'primary' : 'grey-7'"
              dense
              round
              icon="brush"
              @click="mode = 'brush'"
            >
              <q-tooltip>Brush (B)</q-tooltip>
            </q-btn>
            <q-btn
              cursorVisible,
              cursorStyle,
              cursorInnerStyle,
              :color="mode === 'eraser' ? 'primary' : 'grey-7'"
              dense
              round
              icon="sym_o_ink_eraser"
              @click="mode = 'eraser'"
            >
              <q-tooltip>Erase (E)</q-tooltip>
            </q-btn>
          </div>
          <div class="row items-center q-gutter-sm">
            <div class="text-caption">Size</div>
            <q-slider v-model="brushSize" :min="4" :max="120" :step="1" style="width: 160px" />
            <div class="text-caption">Feather</div>
            <q-slider v-model="feather" :min="0" :max="60" :step="1" style="width: 160px" />
            <q-toggle v-model="maskOnly" label="Mask-only" dense color="secondary" />
          </div>
        </div>

        <div class="canvas-wrap" ref="wrapEl">
          <canvas
            ref="canvasEl"
            class="mask-canvas"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @pointerleave="onPointerLeave"
            @pointerenter="onPointerEnter"
          />
          <div v-if="cursorVisible" ref="cursorEl" class="brush-cursor" :style="cursorStyle">
            <div class="brush-cursor--inner" :style="cursorInnerStyle"></div>
          </div>
        </div>
        <div class="text-caption text-grey-5 q-mt-xs">
          Tip: paint the area you want visible. Use Eraser to remove. Toggle "Mask-only" for edge
          checks.
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Reset" @click="resetMask" />
        <q-btn flat color="negative" label="Delete Mask" @click="deleteMask" />
        <q-btn flat label="Cancel" @click="cancel" />
        <q-btn color="primary" label="Save" @click="save" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
  import { defineComponent, ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'

  export default defineComponent({
    name: 'RefineSelectionDialog',
    props: {
      modelValue: { type: Boolean, default: false },
      imageSrc: { type: String, default: '' },
      initialMaskSrc: { type: String, default: '' }
    },
    emits: ['update:modelValue', 'save', 'cancel', 'delete'],
    setup(props, { emit }) {
      const open = ref(!!props.modelValue)
      watch(
        () => props.modelValue,
        (v) => (open.value = !!v)
      )
      watch(open, (v) => emit('update:modelValue', !!v))

      const canvasEl = ref(null)
      const wrapEl = ref(null)
      const cursorEl = ref(null)
      const ctxRef = ref(null)
      const img = new Image()
      const maskCanvas = document.createElement('canvas')
      const maskCtx = maskCanvas.getContext('2d')

      const brushSize = ref(36)
      const feather = ref(18)
      const mode = ref('brush') // 'brush' | 'eraser'
      const maskOnly = ref(false)
      const maskTint = 'rgba(0, 200, 0, 0.4)'
      const cursorVisible = ref(false)

      let drawing = false
      let lastX = 0,
        lastY = 0

      const loadImage = async () =>
        new Promise((resolve) => {
          img.crossOrigin = 'anonymous'
          img.onload = () => resolve(true)
          img.onerror = () => resolve(false)
          img.src = props.imageSrc || ''
        })

      const loadInitialMask = async () =>
        new Promise((resolve) => {
          try {
            const src = props.initialMaskSrc
            if (!src || typeof src !== 'string' || !src.length) return resolve(false)
            const m = new Image()
            m.crossOrigin = 'anonymous'
            m.onload = () => {
              let done = false
              try {
                // Try to read pixels (same-origin/data URLs) to support luminance-based masks
                const tmp = document.createElement('canvas')
                tmp.width = maskCanvas.width
                tmp.height = maskCanvas.height
                const tctx = tmp.getContext('2d')
                tctx.clearRect(0, 0, tmp.width, tmp.height)
                tctx.drawImage(m, 0, 0, tmp.width, tmp.height)
                const imgData = tctx.getImageData(0, 0, tmp.width, tmp.height)
                const buf = tctx.createImageData(tmp.width, tmp.height)
                // Heuristic: if alpha varies, use it; else derive from luminance threshold
                let alphaVaries = false
                for (let i = 0; i < imgData.data.length; i += 4) {
                  const a = imgData.data[i + 3]
                  if (a !== 255) {
                    alphaVaries = true
                    break
                  }
                }
                if (alphaVaries) {
                  for (let i = 0; i < imgData.data.length; i += 4) {
                    const a = imgData.data[i + 3]
                    buf.data[i] = 255
                    buf.data[i + 1] = 255
                    buf.data[i + 2] = 255
                    buf.data[i + 3] = a
                  }
                } else {
                  for (let i = 0; i < imgData.data.length; i += 4) {
                    const r = imgData.data[i]
                    const g = imgData.data[i + 1]
                    const b = imgData.data[i + 2]
                    // Simple luma threshold; tweakable if needed
                    const luma = 0.299 * r + 0.587 * g + 0.114 * b
                    const a = luma >= 128 ? 255 : 0
                    buf.data[i] = 255
                    buf.data[i + 1] = 255
                    buf.data[i + 2] = 255
                    buf.data[i + 3] = a
                  }
                }
                maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
                maskCtx.putImageData(buf, 0, 0)
                done = true
              } catch {
                // Likely CORS-tainted: fallback to alpha-only compositing
              }
              if (!done) {
                try {
                  maskCtx.save()
                  maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
                  maskCtx.globalCompositeOperation = 'source-over'
                  maskCtx.fillStyle = '#ffffff'
                  maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height)
                  maskCtx.globalCompositeOperation = 'destination-in'
                  maskCtx.drawImage(m, 0, 0, maskCanvas.width, maskCanvas.height)
                  maskCtx.restore()
                } catch {
                  /* ignore */
                }
              }
              resolve(true)
            }
            m.onerror = () => resolve(false)
            m.src = src
          } catch {
            resolve(false)
          }
        })

      const initCanvas = async () => {
        const ok = await loadImage()
        const cvs = canvasEl.value
        if (!cvs) return
        const nativeW = ok ? img.naturalWidth || img.width || 0 : 0
        const nativeH = ok ? img.naturalHeight || img.height || 0 : 0
        const maxViewW = Math.min(720, window.innerWidth - 32)
        const scale = ok && nativeW ? Math.min(1, maxViewW / nativeW) : 1
        const viewW = ok && nativeW ? Math.round(nativeW * scale) : 480
        const viewH = ok && nativeH ? Math.round(nativeH * scale) : 320
        cvs.width = viewW
        cvs.height = viewH
        const ctx = (ctxRef.value = cvs.getContext('2d'))
        ctx.clearRect(0, 0, viewW, viewH)
        // Native-resolution mask canvas for accurate save/engine usage
        maskCanvas.width = nativeW || viewW
        maskCanvas.height = nativeH || viewH
        // Draw preview image faintly under mask
        if (ok) {
          ctx.globalAlpha = 0.85
          ctx.drawImage(img, 0, 0, viewW, viewH)
          ctx.globalAlpha = 1
        }
        // Init mask to transparent; then draw any provided initial mask
        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
        await loadInitialMask()
        renderComposite()
      }

      const drawChecker = (ctx, w, h) => {
        const size = 12
        ctx.save()
        ctx.clearRect(0, 0, w, h)
        for (let y = 0; y < h; y += size) {
          for (let x = 0; x < w; x += size) {
            const odd = (x / size + y / size) % 2 === 1
            ctx.fillStyle = odd ? '#cfcfcf' : '#eeeeee'
            ctx.fillRect(x, y, size, size)
          }
        }
        ctx.restore()
      }

      const renderComposite = () => {
        const cvs = canvasEl.value
        const ctx = ctxRef.value
        if (!cvs || !ctx) return
        ctx.clearRect(0, 0, cvs.width, cvs.height)
        if (maskOnly.value) {
          drawChecker(ctx, cvs.width, cvs.height)
          // Build a green-tinted mask preview scaled to view size
          const ov = document.createElement('canvas')
          ov.width = cvs.width
          ov.height = cvs.height
          const octx = ov.getContext('2d')
          octx.clearRect(0, 0, ov.width, ov.height)
          octx.drawImage(
            maskCanvas,
            0,
            0,
            maskCanvas.width,
            maskCanvas.height,
            0,
            0,
            ov.width,
            ov.height
          )
          octx.globalCompositeOperation = 'source-in'
          octx.fillStyle = maskTint
          octx.fillRect(0, 0, ov.width, ov.height)
          ctx.drawImage(ov, 0, 0)
        } else {
          // base image
          if (img.width) {
            ctx.globalAlpha = 0.85
            ctx.drawImage(img, 0, 0, cvs.width, cvs.height)
            ctx.globalAlpha = 1
          }
          // overlay mask as a visible tinted layer clipped by mask alpha, without clearing base
          const ov = document.createElement('canvas')
          ov.width = cvs.width
          ov.height = cvs.height
          const octx = ov.getContext('2d')
          octx.clearRect(0, 0, ov.width, ov.height)
          octx.drawImage(
            maskCanvas,
            0,
            0,
            maskCanvas.width,
            maskCanvas.height,
            0,
            0,
            ov.width,
            ov.height
          )
          octx.globalCompositeOperation = 'source-in'
          octx.fillStyle = maskTint
          octx.fillRect(0, 0, ov.width, ov.height)
          ctx.drawImage(ov, 0, 0)
        }
      }

      const drawStamp = (x, y) => {
        const r = Math.max(1, brushSize.value / 2)
        const grd = maskCtx.createRadialGradient(x, y, Math.max(0, r - feather.value), x, y, r)
        const isErase = mode.value === 'eraser'
        // Erase uses destination-out, which depends on source alpha. Center must be opaque.
        grd.addColorStop(0, isErase ? 'rgba(0,0,0,1)' : 'rgba(255,255,255,1)')
        grd.addColorStop(1, 'rgba(255,255,255,0)')
        maskCtx.save()
        maskCtx.globalCompositeOperation = isErase ? 'destination-out' : 'source-over'
        maskCtx.fillStyle = grd
        maskCtx.beginPath()
        maskCtx.arc(x, y, r, 0, Math.PI * 2)
        maskCtx.fill()
        maskCtx.restore()
      }

      const pointerToCanvas = (evt) => {
        const view = canvasEl.value
        const rect = view.getBoundingClientRect()
        const vx = (evt.clientX - rect.left) * (view.width / rect.width)
        const vy = (evt.clientY - rect.top) * (view.height / rect.height)
        const nx = vx * (maskCanvas.width / view.width)
        const ny = vy * (maskCanvas.height / view.height)
        // Store last pointer client coords for rAF-based DOM update
        lastClientX = evt.clientX
        lastClientY = evt.clientY
        scheduleCursorUpdate()
        return { x: nx, y: ny }
      }

      const onPointerDown = (e) => {
        e.preventDefault()
        canvasEl.value.setPointerCapture?.(e.pointerId)
        drawing = true
        const { x, y } = pointerToCanvas(e)
        lastX = x
        lastY = y
        drawStamp(x, y)
        renderComposite()
      }

      const onPointerMove = (e) => {
        e.preventDefault()
        if (!drawing) return
        const { x, y } = pointerToCanvas(e)
        const steps = Math.max(1, Math.hypot(x - lastX, y - lastY) / (brushSize.value / 3))
        for (let i = 1; i <= steps; i++) {
          const t = i / steps
          drawStamp(lastX + (x - lastX) * t, lastY + (y - lastY) * t)
        }
        lastX = x
        lastY = y
        renderComposite()
      }

      const onPointerUp = (e) => {
        e?.preventDefault?.()
        drawing = false
        try {
          canvasEl.value.releasePointerCapture?.(e.pointerId)
        } catch {
          /* ignore */
        }
        // Keep cursor visible; it will continue to track via window pointermove
      }

      const onPointerLeave = () => {
        cursorVisible.value = false
      }

      const onPointerEnter = (e) => {
        try {
          pointerToCanvas(e)
        } catch {
          /* ignore */
        }
        cursorVisible.value = true
      }

      let cursorRAF = 0
      let lastClientX = 0
      let lastClientY = 0
      const scheduleCursorUpdate = () => {
        if (cursorRAF) return
        cursorRAF = requestAnimationFrame(() => {
          cursorRAF = 0
          try {
            const wrap = wrapEl.value
            const view = canvasEl.value
            const cur = cursorEl.value
            if (!wrap || !view || !cur) {
              // Ensure the cursor stays enabled and try again next frame after mount
              cursorVisible.value = true
              scheduleCursorUpdate()
              return
            }
            const wrect = wrap.getBoundingClientRect()
            const crect = view.getBoundingClientRect()
            // Constrain to canvas bounds for visibility and more intuitive behavior
            const cx = Math.min(Math.max(lastClientX, crect.left), crect.right)
            const cy = Math.min(Math.max(lastClientY, crect.top), crect.bottom)
            const x = cx - wrect.left
            const y = cy - wrect.top
            const inside =
              cx >= crect.left && cx <= crect.right && cy >= crect.top && cy <= crect.bottom
            if (!inside) {
              cursorVisible.value = false
              return
            }
            // Position with left/top and center with translate for exact alignment
            cur.style.left = x + 'px'
            cur.style.top = y + 'px'
            cur.style.transform = 'translate(-50%, -50%)'
            cursorVisible.value = true
          } catch {
            /* ignore */
          }
        })
      }

      const viewScale = computed(() => {
        try {
          const view = canvasEl.value
          if (!view) return 1
          if (!maskCanvas.width) return 1
          return view.width / maskCanvas.width
        } catch {
          return 1
        }
      })

      const cursorStyle = computed(() => {
        const s = Math.max(2, brushSize.value * viewScale.value)
        return {
          width: s + 'px',
          height: s + 'px'
        }
      })

      const cursorInnerStyle = computed(() => {
        const outerR = Math.max(1, brushSize.value / 2)
        const innerR = Math.max(0, outerR - feather.value)
        const d = Math.max(0, innerR * 2 * viewScale.value)
        return { width: d + 'px', height: d + 'px' }
      })

      const resetMask = () => {
        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
        renderComposite()
      }

      const buildMaskOutput = () => {
        try {
          return maskCanvas.toDataURL('image/png')
        } catch {
          return ''
        }
      }

      const buildCutoutOutput = () => {
        // Build a foreground cutout: avatar colors with mask alpha (engine-friendly)
        const out = document.createElement('canvas')
        out.width = maskCanvas.width
        out.height = maskCanvas.height
        const octx = out.getContext('2d')
        octx.clearRect(0, 0, out.width, out.height)
        // If an avatar image is available, composite it with the mask via destination-in
        if (img && (img.naturalWidth || img.width) && (img.naturalHeight || img.height)) {
          octx.drawImage(img, 0, 0, out.width, out.height)
          octx.globalCompositeOperation = 'destination-in'
          octx.drawImage(maskCanvas, 0, 0)
          octx.globalCompositeOperation = 'source-over'
        } else {
          // Fallback: export the mask-only (white with alpha), in case image not loaded
          const imgData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height)
          const buf = octx.createImageData(out.width, out.height)
          for (let i = 0; i < imgData.data.length; i += 4) {
            const a = imgData.data[i + 3]
            buf.data[i] = 255
            buf.data[i + 1] = 255
            buf.data[i + 2] = 255
            buf.data[i + 3] = a
          }
          octx.putImageData(buf, 0, 0)
        }
        return out.toDataURL('image/png')
      }

      const save = () => {
        const maskSrc = buildMaskOutput()
        const cutoutSrc = buildCutoutOutput()
        emit('save', { maskSrc, cutoutSrc })
        open.value = false
      }

      const deleteMask = () => {
        emit('delete')
        open.value = false
      }

      const cancel = () => {
        open.value = false
        emit('cancel')
      }

      let winMoveHandler = null
      onMounted(() => {
        initCanvas()
        winMoveHandler = (e) => {
          lastClientX = e.clientX
          lastClientY = e.clientY
          scheduleCursorUpdate()
        }
        try {
          window.addEventListener('pointermove', winMoveHandler, { passive: true })
        } catch {
          /* ignore */
        }
      })
      onBeforeUnmount(() => {
        try {
          window.removeEventListener('pointermove', winMoveHandler)
        } catch {
          /* ignore */
        }
        if (cursorRAF) cancelAnimationFrame(cursorRAF)
        cursorRAF = 0
        winMoveHandler = null
      })
      watch(
        () => props.imageSrc,
        () => initCanvas()
      )
      watch(
        () => props.initialMaskSrc,
        async () => {
          await loadInitialMask()
          renderComposite()
        }
      )
      watch(
        () => maskOnly.value,
        () => {
          renderComposite()
        }
      )
      watch(open, async (v) => {
        if (v) {
          // Default to showing base image with overlay for clarity
          maskOnly.value = false
          await initCanvas()
        }
      })

      return {
        open,
        canvasEl,
        wrapEl,
        cursorEl,
        brushSize,
        feather,
        mode,
        maskOnly,
        cursorVisible,
        cursorStyle,
        cursorInnerStyle,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerLeave,
        onPointerEnter,
        resetMask,
        save,
        deleteMask,
        cancel
      }
    }
  })
</script>

<style scoped>
  .refine-card {
    min-width: 280px;
    max-width: 92vw;
  }
  .canvas-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  .mask-canvas {
    width: 100%;
    max-width: 720px;
    background: #000;
    border-radius: 6px;
    touch-action: none;
    cursor: none;
  }
  .brush-cursor {
    position: absolute;
    pointer-events: none;
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    will-change: left, top, transform, width, height;
    z-index: 3;
  }
  .brush-cursor--inner {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border: 1px dashed rgba(0, 200, 0, 0.7);
    border-radius: 50%;
  }
  .tools {
    flex-wrap: wrap;
  }
</style>
